import React, { createContext, useContext, useState, ReactNode } from 'react';
import Snackbar, { SnackbarMessage } from '../components/UI/Snackbar';

interface NotificationContextType {
  showNotification: (message: Omit<SnackbarMessage, 'id'>) => void;
  showSuccess: (title: string, message: string, duration?: number) => void;
  showError: (title: string, message: string, duration?: number) => void;
  showWarning: (title: string, message: string, duration?: number) => void;
  showInfo: (title: string, message: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<SnackbarMessage[]>([]);

  const generateId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  const showNotification = (message: Omit<SnackbarMessage, 'id'>) => {
    const id = generateId();
    const newNotification: SnackbarMessage = {
      id,
      duration: message.duration || 4000, // 4 segundos por padrão
      ...message,
    };

    setNotifications(prev => [...prev, newNotification]);
  };

  const showSuccess = (title: string, message: string, duration?: number) => {
    showNotification({
      type: 'success',
      title,
      message,
      duration: duration || 3000, // 3 segundos para sucesso
    });
  };

  const showError = (title: string, message: string, duration?: number) => {
    showNotification({
      type: 'error',
      title,
      message,
      duration: duration || 6000, // 6 segundos para erros
    });
  };

  const showWarning = (title: string, message: string, duration?: number) => {
    showNotification({
      type: 'warning',
      title,
      message,
      duration: duration || 4000, // 4 segundos para avisos
    });
  };

  const showInfo = (title: string, message: string, duration?: number) => {
    showNotification({
      type: 'info',
      title,
      message,
      duration: duration || 3000, // 3 segundos para informações
    });
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      
      {/* Container de notificações */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map((notification) => (
          <Snackbar
            key={notification.id}
            message={notification}
            onClose={removeNotification}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
